// components/HomePage/Footer.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUp,
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  Phone,
} from "lucide-react";

import { WhatsAppIcon } from "../common/WhatsAppButton";
import {
  COMPANY,
  CONTACT,
  MAILTO,
  SOCIAL,
  TEL,
  whatsappUrl,
} from "../../config/site";
import { SERVICES, findService } from "../../data/serviceCatalog";

/**
 * The service column is built from the generated catalog rather than from
 * hand-written hrefs, so a label can never again advertise something the
 * site does not sell and a slug can never drift from the page it opens.
 * Each entry is [categoryKey, slug]; title and URL both come from data.
 */
const FOOTER_SERVICE_KEYS = [
  ["digital-transformation", "web-development"],
  ["digital-transformation", "app-development"],
  ["digital-transformation", "custom-software"],
  ["emerging-tech", "ai-agents-ai-chatbots"],
  ["business-apps", "dynamics-365-erp"],
  ["digital-marketing", "seo"],
];

const FOOTER_SERVICES = FOOTER_SERVICE_KEYS.map(([categoryKey, slug]) =>
  findService(categoryKey, slug)
).filter(Boolean);

/** Real routes only. /careers, /team and /faq do not exist and are gone. */
const COMPANY_LINKS = [
  { to: "/about", label: "About" },
  { to: "/portfolio", label: "Projects" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

const SOCIAL_LINKS = [
  { Icon: Linkedin, href: SOCIAL.linkedin, label: "LinkedIn" },
  { Icon: Facebook, href: SOCIAL.facebook, label: "Facebook" },
  { Icon: Instagram, href: SOCIAL.instagram, label: "Instagram" },
  // Twitter/X was deliberately switched off by the owner — do not re-add.
];

const columnHeadingClass =
  "text-[0.7rem] font-semibold uppercase tracking-eyebrow text-white";

const linkClass =
  "inline-block py-1.5 text-[0.95rem] text-ink-300 transition-colors duration-200 hover:text-white";

const contactLinkClass =
  "group flex items-start gap-3 text-ink-300 transition-colors duration-200 hover:text-white";

const contactIconClass =
  "mt-1 h-4 w-4 flex-none text-ink-500 transition-colors duration-200 group-hover:text-brand-300";

const Footer = () => {
  const [isActive, setIsActive] = useState(false);
  const year = new Date().getFullYear();

  // The mark/wordmark swap is a CSS :hover effect on desktop; below 768px
  // there is no hover, so a tap toggles the same .active state.
  const handleLogoClick = () => {
    if (window.innerWidth < 768) {
      setIsActive((active) => !active);
    }
  };

  const scrollToTop = () => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: prefersReduced ? "auto" : "smooth",
    });
  };

  return (
    <footer className="relative overflow-hidden border-t border-white/10 bg-ink-950 text-ink-300">
      {/* Engineering grid instead of blurred colour blobs; it fades out
          well before the legal line so the text stays clean. */}
      <div
        aria-hidden="true"
        className="grid-field-dark pointer-events-none absolute inset-0 opacity-70 [mask-image:linear-gradient(to_bottom,black,transparent_65%)]"
      />
      {/* Brand hairline sitting on top of the border. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/60 to-transparent"
      />

      <div className="container-custom relative pb-8 pt-16 sm:pt-20 lg:pt-24">
        {/* --------------------------------------------------------------
            The index: identity, services, company, contact.
            -------------------------------------------------------------- */}
        <div className="grid gap-x-8 gap-y-12 lg:grid-cols-12">
          {/* Identity */}
          <div className="lg:col-span-4">
            {/* Was a clickable div that went nowhere; the footer logo is
                the conventional way back to the homepage. */}
            <Link
              to="/"
              aria-label={`${COMPANY.name}, back to home`}
              onClick={handleLogoClick}
              className={`logo-container ${isActive ? "active" : ""}`}
            >
              <img
                src="/BG/Logo.png"
                alt=""
                aria-hidden="true"
                width={120}
                height={70}
                loading="lazy"
                decoding="async"
                className="logo filter invert brightness-0"
              />
              <img
                src="/BG/depthlogix.png"
                alt=""
                aria-hidden="true"
                width={178}
                height={28}
                loading="lazy"
                decoding="async"
                className="logo-text h-7 w-auto filter invert brightness-0"
              />
            </Link>

            <p className="mt-5 max-w-sm text-[0.95rem] leading-relaxed text-ink-400">
              {COMPANY.tagline} We build web and mobile products, AI systems and
              business platforms, and run the marketing around them.
            </p>

            <ul className="mt-8 flex flex-wrap items-center gap-3">
              {SOCIAL_LINKS.map(({ Icon, href, label }) => (
                <li key={label}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${COMPANY.name} on ${label}`}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-ink-300 transition-[background-color,border-color,color] duration-200 hover:border-brand-400/60 hover:bg-brand-500/15 hover:text-white"
                  >
                    <Icon
                      className="h-[1.15rem] w-[1.15rem]"
                      aria-hidden="true"
                    />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Link columns */}
          <div className="grid gap-x-8 gap-y-10 sm:grid-cols-3 lg:col-span-8">
            <nav aria-labelledby="footer-services">
              <h3 id="footer-services" className={columnHeadingClass}>
                Services
              </h3>
              <ul className="mt-5 space-y-0.5">
                {FOOTER_SERVICES.map((service) => (
                  <li key={service.path}>
                    <Link to={service.path} className={linkClass}>
                      {service.title}
                    </Link>
                  </li>
                ))}
                <li className="pt-2">
                  <Link
                    to="/services"
                    className="group inline-flex items-center gap-2 py-1.5 text-[0.95rem] font-medium text-brand-300 transition-colors duration-200 hover:text-white"
                  >
                    All {SERVICES.length} services
                    <ArrowRight
                      className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5"
                      aria-hidden="true"
                    />
                  </Link>
                </li>
              </ul>
            </nav>

            <nav aria-labelledby="footer-company">
              <h3 id="footer-company" className={columnHeadingClass}>
                Company
              </h3>
              <ul className="mt-5 space-y-0.5">
                {COMPANY_LINKS.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className={linkClass}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div>
              <h3 className={columnHeadingClass}>Contact</h3>
              <ul className="mt-5 space-y-3 text-[0.95rem]">
                <li>
                  <a href={MAILTO} className={contactLinkClass}>
                    <Mail className={contactIconClass} aria-hidden="true" />
                    <span className="break-all">{CONTACT.email}</span>
                  </a>
                </li>
                <li>
                  <a href={TEL} className={contactLinkClass}>
                    <Phone className={contactIconClass} aria-hidden="true" />
                    <span>{CONTACT.phoneDisplay}</span>
                  </a>
                </li>
                <li>
                  <a
                    href={whatsappUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={contactLinkClass}
                  >
                    <WhatsAppIcon className={contactIconClass} />
                    <span>Message us on WhatsApp</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* --------------------------------------------------------------
            Legal line. Privacy Policy / Terms pointed at a dead anchor
            and are removed until those pages actually exist.
            -------------------------------------------------------------- */}
        <div className="mt-16 flex flex-col gap-4 border-t border-white/10 pt-7 text-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="text-ink-400">
            © {year} {COMPANY.name}. All Rights Reserved.
          </p>

          {/* sm:mr-20 keeps this clear of the fixed WhatsApp button. */}
          <button
            type="button"
            onClick={scrollToTop}
            className="group inline-flex items-center gap-2 self-start rounded-lg border border-white/10 px-3 py-2 text-xs font-medium text-ink-400 transition-[border-color,color] duration-200 hover:border-brand-400/50 hover:text-white sm:self-auto sm:mr-20"
          >
            Back to top
            <ArrowUp
              className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-y-0.5"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
